export interface Offset {
	x: number;
	y: number;
}

export interface Viewport {
	width: number;
	height: number;
}

/** How fast a wheel tick zooms. Chosen so one notch is a noticeable but not jarring step. */
const ZOOM_PER_PIXEL = 0.0015;

export function clampScale(scale: number, maxScale: number): number {
	if (!Number.isFinite(scale)) return 1;

	return Math.min(Math.max(scale, 1), Math.max(1, maxScale));
}

/**
 * Keeps the content inside the viewport.
 *
 * Smaller than the viewport means centred: a plan that fits should sit in the middle
 * rather than in a corner. Larger means the edge can never come away from the edge of
 * the viewport, so there is no way to lose the plan off-screen.
 */
export function clampOffset(offset: Offset, content: Viewport, viewport: Viewport): Offset {
	const axis = (value: number, contentSize: number, viewportSize: number) => {
		if (contentSize <= viewportSize) return (viewportSize - contentSize) / 2;

		return Math.min(0, Math.max(viewportSize - contentSize, value));
	};

	return {
		x: axis(offset.x, content.width, viewport.width),
		y: axis(offset.y, content.height, viewport.height),
	};
}

/**
 * Zooms around a point, so whatever is under the cursor stays under the cursor.
 *
 * Without this the plan jumps away from the spot you were looking at on every notch.
 */
export function zoomAt(
	pointer: Offset,
	current: { scale: number; offset: Offset },
	nextScale: number,
): { scale: number; offset: Offset } {
	const ratio = nextScale / current.scale;

	return {
		scale: nextScale,
		offset: {
			x: pointer.x - (pointer.x - current.offset.x) * ratio,
			y: pointer.y - (pointer.y - current.offset.y) * ratio,
		},
	};
}

/** Turns a wheel delta into a scale, so a trackpad and a mouse behave the same way. */
export function scaleForWheel(deltaY: number, scale: number, maxScale: number): number {
	return clampScale(scale * Math.exp(-deltaY * ZOOM_PER_PIXEL), maxScale);
}

/** The size the plan is drawn at: fitted to the viewport, then scaled. */
export function contentSize(fit: Viewport, scale: number): Viewport {
	return { width: fit.width * scale, height: fit.height * scale };
}

/** Fits an image inside the viewport without cropping, the same way `contain` does. */
export function fitInside(image: Viewport, viewport: Viewport): Viewport {
	if (image.width <= 0 || image.height <= 0 || viewport.width <= 0 || viewport.height <= 0) {
		return { width: 0, height: 0 };
	}

	const imageAspect = image.width / image.height;
	const viewportAspect = viewport.width / viewport.height;

	return imageAspect > viewportAspect
		? { width: viewport.width, height: viewport.width / imageAspect }
		: { width: viewport.height * imageAspect, height: viewport.height };
}
